/* Copyright (c) 2020-present CloudIO Inc */

import { AnyItem, Page } from '../../registry/ComponentRegistry';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionSetProjection';

const ActionSetProjectionManager: ActionManagerFn<'ActionSetProjection'> = (
  platform: Platform,
): ActionManager<'ActionSetProjection'> => {
  return {
    ...baseActionManager(platform, 'ActionSetProjection'),
    defaultValue,
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return (
        parent.type === 'ActionQuery' || parent.type === 'TriggerBeforeQuery'
      );
    },
    props: {
      ...baseActionProps,
      attributeCode: {
        label: 'Attribute',
        type: 'ViewAttributeChooser',
        required: true,
        aliasType: 'QUERY_FILTER',
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
      },
    },
    name: 'Set Projection',
    getDisplayText: (item) => item.props.attributeCode,
    description: `**Overview**<br/>
The Set Projection action is a query optimization and structuring component designed to specify exactly which attributes (columns) should be retrieved from the database during a query. It corresponds directly to the explicit SELECT clause in SQL (e.g., SELECT Name, Email vs SELECT *).<br/><br/>
This component cannot exist independently; it is strictly an architectural modifier used within ActionQuery or TriggerBeforeQuery. By default, queries often fetch complete records with all available fields. Adding "Set Projection" children to a query instructs the system to ignore unlisted fields, retrieving only the specific data points defined.

**Key Features**<br/>
* **Data Reduction:** Significantly improves performance by minimizing network payload and memory usage, especially when dealing with tables containing large text blocks, images, or hundreds of columns.
* **Result Shaping:** Transitions the query mode from "Fetch All Attributes" to "Fetch Selected Attributes." Once a single projection is defined, only explicitly projected fields are returned.
* **Strict Parent Scope:** Enforces usage validity via isAllowedInsideParent, ensuring it only functions within valid data querying contexts where the result set structure can be modified.
* **Security & Privacy:** Ensures that sensitive or irrelevant columns are not even retrieved from the database, adhering to the principle of least privilege.

**Properties**<br/>
* **Attribute (attributeCode) (Required):** A ViewAttributeChooser property.
  * *Function:* The specific field (column) to include in the query result set.

**Use Cases**<br/>
* **Lightweight Lookups:** Querying a "Users" table to populate a simple dropdown. Using projection to fetch only UserId and FullName, while ignoring heavy fields like Biography, ProfileImage, or AuditLogs.
* **Public APIs:** Defining a query for a public-facing list that strictly returns safe fields (e.g., Title, Date) and strictly excludes internal fields (e.g., CostPrice, SupplierID).
* **Bandwidth Optimization:** Reducing the data transfer size for mobile users by stripping out unused columns from a large data grid.`,
  };
};

export default ActionSetProjectionManager;
